import type { RawParams } from "@uirouter/core";
import { isActionOf } from "typesafe-actions";

import { ABORT_ERROR_NAME } from "app/pages/sales-intelligence/constants/common";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import {
    fetchIntentUserApprovalThunk,
    fetchSelectedIntentSignalTopicsThunk,
    fetchSignalIntentTopicsDescriptionThunk,
} from "app/pages/sales-intelligence/sub-modules/intent/store/signal/effects/effects";
import { notRelevantResultsModalPayloadSetAction } from "app/pages/sales-intelligence/sub-modules/opportunities/store/action-creators";
import { selectAppliedSignals } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";
import type { SignalsDigestParams } from "app/pages/sales-intelligence/sub-modules/signals/types";
import { getFetchResponseErrorMessage } from "app/pages/sales-intelligence/sub-modules/signals/utils/getFetchResponseErrorMessage";
import { getSignalsRoutingParams } from "app/pages/sales-intelligence/sub-modules/signals/utils/routing";
import { is } from "app/pages/sales-intelligence/utils/type-guards";
import type { CommonRequestOptions, GetRequestOptions, PutConfigObject } from "app/services/fetchService";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import type Signal from "../models/core/Signal";
import type SignalFilters from "../models/core/SignalFilters";
import type SignalUpdateDTO from "../models/miscellaneous/SignalUpdateDTO";
import signalsService from "../services/signalsService";

import * as ac from "./action-creators";

export function fetchIntentSignalsDataThunk() {
    return async function fetchIntentSignalsData(dispatch: ThunkDispatchCommon) {
        if (!salesSettingsHelper.isIntentSignalsEnabled()) {
            return;
        }

        const intentApproval = localStorage.getItem("si:intent-approval");

        if (!intentApproval) {
            void dispatch(fetchIntentUserApprovalThunk());
        }

        void dispatch(fetchSelectedIntentSignalTopicsThunk());
        void dispatch(fetchSignalIntentTopicsDescriptionThunk());
    };
}

export function fetchSignalsDescriptionThunk(options?: GetRequestOptions) {
    return async function fetchSignalsDescription(
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(ac.fetchSignalsDescriptionAsync.request());

        try {
            const description = await deps.si.api.signals.fetchSignalsDescription(options);

            return dispatch(ac.fetchSignalsDescriptionAsync.success(description));
        } catch (e) {
            return dispatch(ac.fetchSignalsDescriptionAsync.failure(e));
        }
    };
}

export function fetchSignalsThunk(options?: GetRequestOptions) {
    return async function fetchSignals(
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(ac.fetchSignalsAsync.request());

        try {
            const response = await deps.si.api.signals.fetchSignals(options);
            const groupedById = signalsService.groupSignalsById(response.signals);

            return dispatch(ac.fetchSignalsAsync.success(groupedById));
        } catch (e) {
            if (e?.name === ABORT_ERROR_NAME) {
                return dispatch(ac.fetchSignalsAsync.cancel());
            }

            return dispatch(ac.fetchSignalsAsync.failure(e));
        }
    };
}

function fetchSignalsAndApplyConditionallyThunk(params: RawParams, options?: GetRequestOptions) {
    return async function fetchSignalsAndApplyConditionally(
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(ac.fetchSignalsAsync.request());

        try {
            const response = await deps.si.api.signals.fetchSignals(options);
            const { signalsGroupedById, signalsIdsToApply, haveSignalsToApply } =
                signalsService.getPreAppliedSignalsBasedOnParams(
                    params,
                    signalsService.groupSignalsById(response.signals),
                );

            if (haveSignalsToApply) {
                dispatch(ac.appliedSignalsChangedAction(signalsIdsToApply));
            }

            return dispatch(ac.fetchSignalsAsync.success(signalsGroupedById));
        } catch (e) {
            if (e?.name === ABORT_ERROR_NAME) {
                return dispatch(ac.fetchSignalsAsync.cancel());
            }

            return dispatch(ac.fetchSignalsAsync.failure(e));
        }
    };
}

export function fetchSignalsAndApplyBasedOnParamsThunk(params: RawParams, options?: GetRequestOptions) {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const signalsParams = getSignalsRoutingParams(params);

        const resultAction = await dispatch(fetchSignalsAndApplyConditionallyThunk(signalsParams, options));

        const isSuccessAction = isActionOf(ac.fetchSignalsAsync.success);
        const hasAppliedSignals = isSuccessAction(resultAction) && selectAppliedSignals(getState()).length > 0;

        const shouldCheckSignalsResultsRelevance = !is.undefined(signalsParams.digestDate) && hasAppliedSignals;

        if (shouldCheckSignalsResultsRelevance) {
            const isDigestExpired = signalsService.isSignalsDigestExpired(signalsParams as SignalsDigestParams);

            if (isDigestExpired) {
                const { signalId } = signalsParams as SignalsDigestParams;
                const normalizedSignalId: string = Array.isArray(signalId) ? signalId[0] : signalId;

                dispatch(
                    notRelevantResultsModalPayloadSetAction({
                        signalId: normalizedSignalId,
                    }),
                );
            }
        }
    };
}

export function updateSignalThunk<F extends SignalFilters>(dto: SignalUpdateDTO<F>, options?: PutConfigObject) {
    return async function createSignal(
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        try {
            const signal = await deps.si.api.signals.updateSignal(dto, options);

            return dispatch(ac.updateSignalAsync.success(signal));
        } catch (e) {
            if (e?.name === ABORT_ERROR_NAME) {
                return dispatch(ac.updateSignalAsync.cancel());
            }

            return dispatch(ac.updateSignalAsync.failure(e));
        }
    };
}

export function deleteSignalThunk(signalId: Signal["id"], options?: CommonRequestOptions) {
    return async function deleteSignal(
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(ac.deleteSignalsAsync.request(signalId));

        try {
            await deps.si.api.signals.deleteSignal(signalId, options);

            return dispatch(ac.deleteSignalsAsync.success(signalId));
        } catch (e) {
            const error = await getFetchResponseErrorMessage(e, "detail");

            return dispatch(ac.deleteSignalsAsync.failure({ signalId, error }));
        }
    };
}
